import React from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {FormattedMessage, useIntl} from "react-intl";
import {InputLabel, Select, SelectOptionProps, Toggle} from "peggirkit";
import {toggleLabel} from "peggirkit/dist/components/Input/Toggle/Single/Label/DefaultToggleLabel";
import {
    CategoryPlanningFormValues,
    DayPlanning,
    DayPlanningMode,
} from "@widgets/menuEditor/menu/categories/category/planning/categoryPlanning";

/** `00:00`, `00:30`, ..., `23:30` */
const HALF_HOURS: string[] = Array.from({length: 48}, (_, i) =>
    `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 === 0 ? "00" : "30"}`);

/**
 * Empty option first (start/end of the day), then every half hour. A current
 * time that isn't on the half hour (e.g. from Sitedish) is kept as an option.
 */
const timeOptions = (emptyLabel: string, current: string): SelectOptionProps<string>[] => {
    const times: string[] = current !== "" && !HALF_HOURS.includes(current)
        ? [...HALF_HOURS, current].sort()
        : HALF_HOURS;

    return [
        {value: "", displayValue: emptyLabel},
        ...times.map(time => ({value: time, displayValue: time})),
    ];
};

type Props = {
    index: number,
    disabled: boolean,
};

/**
 * Planning of one day: available all day, or (custom) closed or only from
 * and/or until a time.
 */
const DayPlanningRow = ({index, disabled}: Props) => {
    const intl = useIntl();
    const {control} = useFormContext<CategoryPlanningFormValues>();
    const day: DayPlanning = useWatch({control, name: `days.${index}`});
    const modeOptions: SelectOptionProps<string>[] = [
        {value: "times", displayValue: intl.formatMessage({id: "planning.mode.times"})},
        {value: "closed", displayValue: intl.formatMessage({id: "planning.mode.closed"})},
    ];
    const showTimes: boolean = day.custom && day.mode === "times";

    return (
        <li className={"w-full flex flex-col gap-4 py-4"}>
            <div className={"w-full flex flex-row items-center justify-between gap-4"}>
                <div className={"flex flex-col"}>
                    <span className={"font-medium text-gray-900 dark:text-white"}>
                        <FormattedMessage id={`day.${day.day}`}/>
                    </span>
                    {
                        !day.custom &&
                        <span className={"text-sm text-gray-500 dark:text-slate-400"}>
                            <FormattedMessage id={"planning.allDay"}/>
                        </span>
                    }
                </div>

                <Controller
                    control={control}
                    name={`days.${index}.custom`}
                    render={({field: {value, onChange}}) => (
                        <Toggle
                            type={"checkbox"}
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                            label={toggleLabel(intl.formatMessage({id: "planning.custom"}))}
                            disabled={disabled}
                        />
                    )}
                />
            </div>

            {
                day.custom &&
                <div className={"w-full grid grid-cols-1 sm:grid-cols-3 gap-4"}>
                    <Controller
                        control={control}
                        name={`days.${index}.mode`}
                        render={({field: {value, onChange}}) => (
                            <Select<string>
                                label={<InputLabel><FormattedMessage id={"planning.mode"}/></InputLabel>}
                                options={modeOptions}
                                value={modeOptions.find(option => option.value === value) ?? null}
                                displayValueEmpty={"-"}
                                onChange={(option: SelectOptionProps<string>) => onChange(option.value as DayPlanningMode)}
                                disabled={disabled}
                            />
                        )}
                    />

                    {
                        showTimes &&
                        <>
                            <Controller
                                control={control}
                                name={`days.${index}.timeFrom`}
                                rules={{
                                    validate: (timeFrom: string, form: CategoryPlanningFormValues) => {
                                        const {custom, mode, timeUntil} = form.days[index];
                                        if (!custom || mode !== "times") return true;
                                        if (timeFrom === "" && timeUntil === "")
                                            return intl.formatMessage({id: "planning.enterFromOrUntil"});
                                        return true;
                                    },
                                }}
                                render={({field: {value, onChange}, fieldState}) => {
                                    const options = timeOptions(intl.formatMessage({id: "planning.startOfDay"}), value);
                                    return (
                                        <Select<string>
                                            label={<InputLabel><FormattedMessage id={"planning.from"}/></InputLabel>}
                                            options={options}
                                            value={options.find(option => option.value === value) ?? null}
                                            displayValueEmpty={"-"}
                                            onChange={(option: SelectOptionProps<string>) => onChange(option.value)}
                                            disabled={disabled}
                                            error={fieldState.error?.message}
                                        />
                                    );
                                }}
                            />

                            <Controller
                                control={control}
                                name={`days.${index}.timeUntil`}
                                rules={{
                                    validate: (timeUntil: string, form: CategoryPlanningFormValues) => {
                                        const {custom, mode, timeFrom} = form.days[index];
                                        if (!custom || mode !== "times") return true;
                                        // `HH:mm` compares correctly as text
                                        if (timeFrom !== "" && timeUntil !== "" && timeFrom >= timeUntil)
                                            return intl.formatMessage({id: "planning.fromBeforeUntil"});
                                        return true;
                                    },
                                }}
                                render={({field: {value, onChange}, fieldState}) => {
                                    const options = timeOptions(intl.formatMessage({id: "planning.endOfDay"}), value);
                                    return (
                                        <Select<string>
                                            label={<InputLabel><FormattedMessage id={"planning.until"}/></InputLabel>}
                                            options={options}
                                            value={options.find(option => option.value === value) ?? null}
                                            displayValueEmpty={"-"}
                                            onChange={(option: SelectOptionProps<string>) => onChange(option.value)}
                                            disabled={disabled}
                                            error={fieldState.error?.message}
                                        />
                                    );
                                }}
                            />
                        </>
                    }
                </div>
            }
        </li>
    );
};

export default DayPlanningRow;